import React from 'react';
import { X, Play, Youtube, Facebook, Instagram } from 'lucide-react';
import { ErrorHandlingMedia } from '../../common/ErrorHandlingMedia';
import { PdfPreview } from '../../common/PdfPreview';

// Component for social media video placeholders
const SocialVideoPlaceholder: React.FC<{ url: string }> = ({ url }) => {
  const getPlatformInfo = (url: string) => {
    const lowerUrl = url.toLowerCase();
    if (lowerUrl.includes('youtube.com') || lowerUrl.includes('youtu.be')) {
      return { name: 'YouTube', icon: Youtube, color: 'text-red-600' };
    }
    if (lowerUrl.includes('facebook.com')) {
      return { name: 'Facebook', icon: Facebook, color: 'text-blue-600' };
    }
    if (lowerUrl.includes('instagram.com')) {
      return { name: 'Instagram', icon: Instagram, color: 'text-pink-600' };
    }
    return { name: 'Video', icon: Play, color: 'text-gray-600' };
  };

  const platform = getPlatformInfo(url);
  const IconComponent = platform.icon;

  return (
    <div className="w-full h-full bg-gray-100 flex flex-col items-center justify-center p-4">
      <div className="flex flex-col items-center justify-center space-y-3">
        <div className="relative">
          <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center">
            <IconComponent className={`w-8 h-8 ${platform.color}`} />
          </div>
          <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-md">
            <Play className="w-4 h-4 text-gray-600 ml-0.5" fill="currentColor" />
          </div>
        </div>
        <div className="text-center">
          <p className="text-sm font-medium text-gray-700">{platform.name} Video</p>
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center mt-2 px-3 py-1 bg-primary-600 text-white text-xs rounded-full hover:bg-primary-700 transition-colors"
          >
            View Video
          </a>
        </div>
      </div>
    </div>
  );
};

interface MediaGridProps {
  existingMediaUrls: string[];
  multipleMedia: File[];
  singleMedia: File | null;
  existingMediaUrl?: string | null;
  existingMediaType?: 'image' | 'video' | null;
  onRemoveSingle: () => void;
  onRemoveMulti: (index: number) => void;
  onRemoveExistingUrl?: (index: number) => void;
}

export const MediaGrid: React.FC<MediaGridProps> = ({
  existingMediaUrls,
  multipleMedia,
  singleMedia,
  existingMediaUrl,
  existingMediaType,
  onRemoveSingle,
  onRemoveMulti,
  onRemoveExistingUrl,
}) => {
  const isPdf = (f: File | null) => Boolean(f && (f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf')));
  const isVideo = (f: File | null) => Boolean(f && (f.type === 'video/mp4' || f.type === 'video/quicktime' || f.name.toLowerCase().match(/\.(mp4|mov)$/)));
  const isDirectVideoUrl = (url: string) => {
    const lowerUrl = url.toLowerCase();
    return lowerUrl.match(/\.(mp4|mov)$/);
  };

  const isSocialVideoUrl = (url: string) => {
    const lowerUrl = url.toLowerCase();
    return (lowerUrl.includes('youtube.com') || lowerUrl.includes('youtu.be')) ||
           (lowerUrl.includes('facebook.com') && lowerUrl.includes('/videos/')) ||
           (lowerUrl.includes('instagram.com') && (lowerUrl.includes('/reel/') || lowerUrl.includes('/p/')));
  };

  const isVideoUrl = (url: string) => isDirectVideoUrl(url) || isSocialVideoUrl(url);

  const singleMediaCount = (singleMedia ? 1 : 0) + (existingMediaUrl ? 1 : 0);
  const allCount = existingMediaUrls.length + multipleMedia.length + singleMediaCount;
  const gridCols = allCount === 1 ? 'grid-cols-1' : allCount === 2 ? 'grid-cols-2' : 'grid-cols-3';

  return (
    <div className={`grid ${gridCols} gap-3`}>
      {existingMediaUrls.map((url, idx) => (
        <div key={`url-${idx}`} className="relative rounded-lg overflow-hidden bg-gray-100">
          <div className="w-full" style={{ aspectRatio: '4 / 5' }}>
            {isDirectVideoUrl(url) ? (
              <ErrorHandlingMedia src={url} alt={`Video ${idx + 1}`} type="video" className="w-full h-full object-cover" controls={true} />
            ) : isSocialVideoUrl(url) ? (
              <SocialVideoPlaceholder url={url} />
            ) : (
              <ErrorHandlingMedia src={url} alt={`Image ${idx + 1}`} type="image" className="w-full h-full object-cover" controls={false} />
            )}
          </div>
          {onRemoveExistingUrl && (
            <button
              onClick={() => onRemoveExistingUrl(idx)}
              className="absolute top-2 right-2 p-1 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-75"
              title="Remove media"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      ))}

      {multipleMedia.map((file, idx) => (
        <div key={`file-${idx}`} className="relative rounded-lg overflow-hidden bg-gray-100">
          <div className="w-full" style={{ aspectRatio: '4 / 5' }}>
            {isVideo(file) ? (
              <ErrorHandlingMedia src={URL.createObjectURL(file)} alt={`Video ${idx + 1}`} type="video" className="w-full h-full object-cover" controls={true} />
            ) : isPdf(file) ? (
              <PdfPreview file={file} className="w-full h-full" />
            ) : (
              <ErrorHandlingMedia src={URL.createObjectURL(file)} alt={`Image ${idx + 1}`} type="image" className="w-full h-full object-cover" controls={false} />
            )}
          </div>
          <button
            onClick={() => onRemoveMulti(idx)}
            className="absolute top-2 right-2 p-1 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-75"
            title="Remove media"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ))}

      {singleMedia && existingMediaUrls.length === 0 && multipleMedia.length === 0 && (
        <div key="single-media" className="relative rounded-lg overflow-hidden bg-gray-100">
          <div className="w-full" style={{ aspectRatio: '4 / 5' }}>
            {isVideo(singleMedia) ? (
              <ErrorHandlingMedia src={URL.createObjectURL(singleMedia)} alt="Video" type="video" className="w-full h-full object-cover" controls={true} />
            ) : isPdf(singleMedia) ? (
              <PdfPreview file={singleMedia} className="w-full h-full" />
            ) : (
              <ErrorHandlingMedia src={URL.createObjectURL(singleMedia)} alt="Image" type="image" className="w-full h-full object-cover" controls={false} />
            )}
          </div>
          <button
            onClick={onRemoveSingle}
            className="absolute top-2 right-2 p-1 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-75"
            title="Remove media"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {existingMediaUrl && existingMediaUrls.length === 0 && multipleMedia.length === 0 && !singleMedia && (
        <div key="single-existing" className="relative rounded-lg overflow-hidden bg-gray-100">
          <div className="w-full" style={{ aspectRatio: '4 / 5' }}>
            {existingMediaType === 'video' && isDirectVideoUrl(existingMediaUrl) ? (
              <ErrorHandlingMedia src={existingMediaUrl} alt="Video" type="video" className="w-full h-full object-cover" controls={true} />
            ) : existingMediaType === 'video' && isSocialVideoUrl(existingMediaUrl) ? (
              <SocialVideoPlaceholder url={existingMediaUrl} />
            ) : existingMediaUrl.toLowerCase().endsWith('.pdf') ? (
              <PdfPreview url={existingMediaUrl} className="w-full h-full" />
            ) : (
              <ErrorHandlingMedia src={existingMediaUrl} alt="Image" type="image" className="w-full h-full object-cover" controls={false} />
            )}
          </div>
          <button
            onClick={onRemoveSingle}
            className="absolute top-2 right-2 p-1 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-75"
            title="Remove media"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};


